import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box, Typography, Snackbar, Alert, TextField, Tooltip, Link, Paper, Select, MenuItem, InputAdornment, IconButton, Button,
  Dialog, DialogTitle, DialogContent, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, TablePagination
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import axios from "../../utils/axios";
import config from "../../config";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function ClickLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, text: "", severity: "success" });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [total, setTotal] = useState(0);

  // State cho chi tiết từng link
  const [showDetail, setShowDetail] = useState(false);
  const [detailLogs, setDetailLogs] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedShortCode, setSelectedShortCode] = useState("");
  const [detailPage, setDetailPage] = useState(0);
  const [detailTotal, setDetailTotal] = useState(0);

  const token = localStorage.getItem("token");

  // FETCH logs từ clicks (gộp theo short_code, đếm click_count)
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = [
        `page=${page + 1}`,
        `pageSize=${pageSize}`,
        search ? `q=${encodeURIComponent(search)}` : ""
      ].filter(Boolean).join("&");
      const res = await axios.get(
        `/teolucpro/clicks?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Gộp log theo short_code
      const rawData = res.data.clicks || [];
      const grouped = {};
      for (const item of rawData) {
        const key = item.links?.short_code;
        if (!key) continue;
        if (!grouped[key]) {
          grouped[key] = {
            ...item,
            short_code: item.links?.short_code,
            link_name: item.links?.link_name,
            original_url: item.links?.original_url,
            clicked_at: item.clicked_at ? item.clicked_at.replace("T", " ").slice(0, 19) : "",
            click_count: 1,
          };
        } else {
          grouped[key].click_count += 1;
        }
      }
      setLogs(Object.values(grouped));
      setTotal(Object.keys(grouped).length);
    } catch (e) {
      setSnack({ open: true, text: "Lỗi tải log clicks!", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [token, search, page, pageSize]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Lấy chi tiết click từng short_code
  const handleShowDetail = useCallback((short_code) => {
    setSelectedShortCode(short_code);
    setShowDetail(true);
    setDetailPage(0);
  }, []);

  useEffect(() => {
    if (showDetail && selectedShortCode) {
      fetchDetailLogs(selectedShortCode, detailPage + 1);
    }
    // eslint-disable-next-line
  }, [showDetail, selectedShortCode, detailPage]);

  const fetchDetailLogs = async (short_code, page = 1) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(
        `/teolucpro/clicks/detail?short_code=${short_code}&page=${page}&pageSize=20`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDetailLogs(res.data.logs);
      setDetailTotal(res.data.total);
    } catch (e) {
      setSnack({ open: true, text: "Lỗi tải log chi tiết!", severity: "error" });
    } finally {
      setDetailLoading(false);
    }
  };

  const columns = useMemo(() => [
    {
      accessorKey: "id",
      header: "ID",
      size: 55,
      cell: info => <b>{info.getValue()}</b>,
    },
    {
      accessorKey: "short_code",
      header: "Short Code",
      size: 122,
      cell: info => info.getValue() ? (
        <Tooltip title={config.BASE_URL + "/" + info.getValue()}>
          <Link
            href={`${config.BASE_URL}/${info.getValue()}`}
            target="_blank"
            underline="hover"
            sx={{
              fontWeight: 700,
              color: "#1976d2",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: .3,
              borderRadius: 2,
              transition: "background 0.2s",
              "&:hover": {
                textDecoration: "underline",
                bgcolor: "#e3f2fd"
              }
            }}
          >
            <OpenInNewIcon fontSize="small" sx={{ mr: 0.2, color: "#90caf9" }} />
            {info.getValue()}
          </Link>
        </Tooltip>
      ) : ""
    },
    {
      accessorKey: "original_url",
      header: "Link gốc",
      size: 240,
      cell: info => info.getValue() ? (
        <Tooltip title={info.getValue()}>
          <Link
            href={info.getValue()}
            target="_blank"
            underline="hover"
            sx={{
              maxWidth: 220,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "block",
              color: "#1565c0"
            }}
          >
            {info.getValue()}
          </Link>
        </Tooltip>
      ) : ""
    },
    {
      accessorKey: "link_name",
      header: "Tên link",
      size: 250,
      cell: info => info.getValue() ? (
        <Tooltip title={info.getValue()}>
          <span style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "block",
            fontWeight: 500,
            color: "#374151"
          }}>
            {info.getValue()}
          </span>
        </Tooltip>
      ) : ""
    },
    {
      accessorKey: "clicked_at",
      header: "Thời gian",
      size: 130,
      cell: info => <span style={{ fontSize: 13 }}>{info.getValue()}</span>
    },
    {
      accessorKey: "ip",
      header: "IP",
      size: 105,
      cell: info => (
        <Tooltip title={info.getValue() || ""}><span>{info.getValue()}</span></Tooltip>
      ),
    },
    {
      accessorKey: "country",
      header: "Quốc gia",
      size: 90,
      cell: info => <span>{info.getValue()}</span>,
    },
    {
      accessorKey: "browser",
      header: "Browser",
      size: 90,
      cell: info => (
        <Tooltip title={info.getValue() || ""}><span>{info.getValue()}</span></Tooltip>
      ),
    },
    // THAY CỘT PLATFORM THÀNH LƯỢT CLICK
    {
      accessorKey: "click_count",
      header: "Lượt click",
      size: 90,
      cell: info => <b style={{ color: "#0d47a1" }}>{info.getValue()}</b>
    },
    {
      accessorKey: "user_agent",
      header: "User Agent",
      size: 200,
      cell: info => (
        <Tooltip title={info.getValue() || ""}>
          <span style={{
            maxWidth: 170,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "block",
            fontSize: 13,
            color: "#888"
          }}>{info.getValue()}</span>
        </Tooltip>
      ),
    },
    {
      accessorKey: "referrer",
      header: "Referrer",
      size: 160,
      cell: info => (
        <Tooltip title={info.getValue() || ""}>
          <span style={{
            maxWidth: 120,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "block"
          }}>{info.getValue()}</span>
        </Tooltip>
      ),
    },
    // Thêm nút Chi tiết
    {
      header: "Hành động",
      accessorKey: "actions",
      size: 90,
      cell: info => (
        <Button
          size="small"
          variant="outlined"
          onClick={() => handleShowDetail(info.row.original.short_code)}
        >
          Chi tiết
        </Button>
      ),
    }
  ], [handleShowDetail]);

  // Table instance
  const table = useReactTable({
    data: logs,
    columns,
    state: {
      pagination: {
        pageIndex: page,
        pageSize,
      }
    },
    manualPagination: true,
    pageCount: Math.ceil(total / pageSize),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Custom Pagination button (MUI style)
  function MuiPaginateBtn({ onClick, disabled, children, icon }) {
    return (
      <IconButton
        onClick={onClick}
        disabled={disabled}
        size="small"
        sx={{
          border: "1px solid #1976d2",
          color: "#1976d2",
          mx: 0.5,
          bgcolor: "#fff",
          "&:hover": { bgcolor: "#1976d2", color: "#fff" },
          borderRadius: 1.5,
          ...(disabled && {
            opacity: 0.6, bgcolor: "#f5f5f5", borderColor: "#ddd", color: "#bdbdbd"
          })
        }}
      >
        {icon || children}
      </IconButton>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 3 }}>
        Lịch sử Click chi tiết (Click Logs)
      </Typography>
      <TextField
        placeholder="Tìm kiếm Short code, tên link, IP, User Agent..."
        size="small"
        value={search}
        onChange={e => { setSearch(e.target.value); setPage(0); }}
        sx={{
          mb: 2, maxWidth: 360, bgcolor: "#fff", borderRadius: 2,
          boxShadow: "0 2px 12px #6366f122"
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <OpenInNewIcon sx={{ color: "#1976d2", fontSize: 20, opacity: 0.6 }} />
            </InputAdornment>
          ),
        }}
      />
      <Paper
        elevation={2}
        sx={{
          borderRadius: 3,
          p: 2,
          bgcolor: "#fff",
          boxShadow: "0 8px 40px #6366f119",
          overflow: "visible"
        }}
      >
        <Box sx={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}>
          <Box sx={{
            maxHeight: 500,
            minHeight: 230,
            overflowY: "auto",
            borderRadius: 2,
            '&::-webkit-scrollbar-thumb': { bgcolor: "#b2dfdb" },
            '&::-webkit-scrollbar': { height: 8, borderRadius: 8 }
          }}>
            <table
              style={{
                borderCollapse: "separate",
                borderSpacing: 0,
                width: "100%",
                tableLayout: "fixed",
                background: "#fff",
                borderRadius: 16,
                boxShadow: "0 1px 6px #e3f2fd"
              }}
            >
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        style={{
                          fontWeight: 700,
                          background: "#f5f8fa",
                          color: "#1976d2",
                          padding: "10px 8px",
                          borderBottom: "1.5px solid #e0e0e0",
                          fontSize: 15,
                          textAlign: "center",
                          position: "sticky",
                          top: 0,
                          zIndex: 2
                        }}
                        colSpan={header.colSpan}
                      >
                        {header.isPlaceholder ? null : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={columns.length} style={{ textAlign: "center", padding: 44 }}>
                      Đang tải dữ liệu...
                    </td>
                  </tr>
                ) : table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} style={{ textAlign: "center", padding: 44, color: "#888" }}>
                      Không có dữ liệu.
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row, idx) => (
                    <tr key={row.id}
                      style={{
                        borderRadius: 14,
                        background: idx % 2 === 0 ? "#fafbfc" : "#fff",
                        transition: "background .18s"
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f0f7ff"}
                      onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? "#fafbfc" : "#fff"}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id}
                          style={{
                            padding: "8px 8px",
                            fontSize: 15,
                            textAlign: "center",
                            borderBottom: "1px solid #f0f0f0",
                            verticalAlign: "middle",
                            background: "inherit"
                          }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Box>
        </Box>
        {/* Pagination */}
        <Box sx={{
          mt: 2, display: "flex", alignItems: "center", justifyContent: "space-between"
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <span style={{ fontSize: 15 }}>Rows per page:</span>
            <Select
              value={pageSize}
              size="small"
              onChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}
              sx={{
                minWidth: 66,
                borderRadius: 2,
                fontSize: 15,
                boxShadow: "0 1px 5px #e0e7ef2a"
              }}
            >
              {PAGE_SIZE_OPTIONS.map(sz =>
                <MenuItem value={sz} key={sz}>{sz}</MenuItem>
              )}
            </Select>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <MuiPaginateBtn
              onClick={() => setPage(0)}
              disabled={page === 0}
              icon={<ArrowBackIosNewIcon sx={{ fontSize: 15, mr: "-2px" }} />}
            />
            <MuiPaginateBtn
              onClick={() => setPage(page - 1)}
              disabled={page === 0}
              icon={<ArrowBackIosNewIcon sx={{ fontSize: 16 }} />}
            />
            <span style={{ fontWeight: 600, margin: "0 8px", fontSize: 15, color: "#374151" }}>
              {page + 1} / {Math.max(1, table.getPageCount())}
            </span>
            <MuiPaginateBtn
              onClick={() => setPage(page + 1)}
              disabled={page >= table.getPageCount() - 1}
              icon={<ArrowForwardIosIcon sx={{ fontSize: 16 }} />}
            />
            <MuiPaginateBtn
              onClick={() => setPage(table.getPageCount() - 1)}
              disabled={page >= table.getPageCount() - 1}
              icon={<ArrowForwardIosIcon sx={{ fontSize: 15, ml: "-2px" }} />}
            />
          </Box>
        </Box>
      </Paper>

      {/* Modal chi tiết từng lần click */}
      <Dialog open={showDetail} onClose={() => setShowDetail(false)} maxWidth="md" fullWidth>
        <DialogTitle>Chi tiết Click: {selectedShortCode}</DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Thời gian</TableCell>
                    <TableCell>IP</TableCell>
                    <TableCell>Browser</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell>User Agent</TableCell>
                    <TableCell>Referrer</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailLogs.map((log, i) => (
                    <TableRow key={i}>
                      <TableCell>{log.clicked_at}</TableCell>
                      <TableCell>{log.ip}</TableCell>
                      <TableCell>{log.browser}</TableCell>
                      <TableCell>{log.country}</TableCell>
                      <TableCell>{log.user_agent}</TableCell>
                      <TableCell>{log.referrer}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={detailTotal}
                page={detailPage}
                onPageChange={(e, newPage) => setDetailPage(newPage)}
                rowsPerPage={20}
                onRowsPerPageChange={() => {}}
                rowsPerPageOptions={[20]}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={2200}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.severity} sx={{ width: "100%" }}>{snack.text}</Alert>
      </Snackbar>
    </Box>
  );
}
